/**
 * Regional product pricing — canonical client fallback and hydration map.
 *
 * Product-specific model. No platform subscription.
 *   - BGMI / Free Fire: free full access with completion records.
 *   - WORK: approved combined pathway / assessment / credential offers.
 *   - Preview, eligibility and enrollment remain independently gated.
 *
 * All amounts are GST-inclusive (18% is extracted server-side at order time).
 * The authoritative prices live in `public.pricing_skus`; these constants exist
 * only so marketing surfaces can render a price without a round-trip, and must
 * be kept in sync with the Pricing & Promos admin.
 */

export type PathwayPricing = {
  /** `pricing_skus.code` for the pathway unlock. */
  pathwaySku: string;
  /** `pricing_skus.code` for the certification exam. */
  certificateSku: string;
  /** `pricing_skus.code` for the pathway + certification bundle. */
  bundleSku: string;
  /** Sticker price of the pathway unlock, in whole rupees (GST-inclusive). */
  pathwayInr: number;
  /** Certification exam price, in whole rupees (GST-inclusive). */
  certificateInr: number;
  /** Bundle price, in whole rupees (GST-inclusive). */
  bundleInr: number;
  /**
   * WORK-pathway model: pathway and certificate are only ever sold together
   * at a single combined price — no individual pathway/certificate SKUs.
   * When true, `pathwayInr`/`certificateInr` are unused and `bundleInr` is
   * the only price surfaces should show.
   */
  bundleOnly?: boolean;
  /** Launch promo code + the price it lands on, if a promo is running. */
  launchPromo?: { code: string; priceInr: number };
};

/**
 * Legacy/default paid-path fallback, retained for existing hydration behavior.
 * These values do not describe current free PLAY or product-specific WORK offers.
 * A new product/price still requires an explicitly approved active SKU.
 */
export const FLAT_PRICING = {
  pathwayInr: 399,
  /** Aligned so path + cert − bundle = exactly ₹100 (399 + 300 − 599). */
  certificateInr: 300,
  bundleInr: 599,
} as const;

/**
 * No public launch promo by default — list price is the standard.
 * Partner codes (PlayDen etc.) are invite-only (`advertise_public=false`)
 * and are not hydrated into marketing surfaces.
 */

/** Mutable holder for the currently advertised promo (null = none running). */
let ACTIVE_PROMO: { code: string; priceInr: number } | null = null;

/** The promo currently advertised on public surfaces, or null when none runs. */
export const getLaunchPromo = () => ACTIVE_PROMO;

/**
 * @deprecated Read `getLaunchPromo()` so the code/price follow the admin.
 * Kept only as a static placeholder for input hints.
 */
export const LAUNCH_PROMO = { code: "PLAYDEN", priceInr: 299 };

const FALLBACK_PATHWAY_PRICING: Record<string, PathwayPricing> = {
  // BGMI Rank-Up is free (2026-10-04): ₹0 pathway SKU with a completion
  // record; certificate and bundle SKUs deactivated, mirroring Free Fire.
  "bgmi-fundamentals": {
    pathwaySku: "pathway_bgmi_rank_up",
    certificateSku: "",
    bundleSku: "",
    pathwayInr: 0,
    certificateInr: 0,
    bundleInr: 0,
  },
  // Free Fire Rank-Up is free: every module, quiz and checkpoint at ₹0, with a
  // completion record on finishing. Its certificate and bundle are retired
  // (SKUs deactivated), so no certificate price is advertised anywhere.
  "free-fire-fundamentals": {
    pathwaySku: "pathway_free_fire_rank_up",
    certificateSku: "",
    bundleSku: "",
    pathwayInr: 0,
    certificateInr: 0,
    bundleInr: 0,
  },
  // Esports Educator (WORK band) — single combined price: ₹2,999 for the
  // pathway and certificate together. There are no individual pathway or
  // certificate SKUs; `bundleOnly` keeps every surface on the one price.
  // Active priced SKUs do not open enrollment. EDUCATOR_LAUNCH_READY and the
  // hosted enrollment_open flag independently gate the approved release.
  "esports-educator": {
    pathwaySku: "",
    certificateSku: "",
    bundleSku: "bundle_esports_educator",
    pathwayInr: 2999,
    certificateInr: 2999,
    bundleInr: 2999,
    bundleOnly: true,
  },
  // Event Operations (WORK Skill Credential): focused ~2h professional competency.
  // Lower tier than the ~11h Educator Professional Certification. Server
  // prices from pricing_skus remain authoritative.
  "event-tournament-operations": {
    pathwaySku: "",
    certificateSku: "",
    bundleSku: "bundle_eventops",
    pathwayInr: 1499,
    certificateInr: 1499,
    bundleInr: 1499,
    bundleOnly: true,
  },
};

/**
 * Live pricing map. Seeded with the fallback constants so the first paint has
 * a price, then hydrated from `public.pricing_skus` + `public.public_promos`
 * by `usePricingSkus()` (see src/hooks/usePricingSkus.ts). Read it through
 * `getPathwayPricing()` — never copy the values into another constant.
 */
export const PATHWAY_PRICING: Record<string, PathwayPricing> = JSON.parse(
  JSON.stringify(FALLBACK_PATHWAY_PRICING),
);

export type PricingSkuRow = {
  code: string;
  sku_type: string;
  pathway_slug: string | null;
  amount_paise: number;
  /** "INR" (default) or "USD". Minor units either way (paise / cents). */
  currency?: string | null;
};

/**
 * Non-INR prices, keyed by currency then pathway slug. Sourced from the same
 * `pricing_skus` rows; kept apart from `PATHWAY_PRICING` so nothing in the
 * India path can accidentally read a dollar amount as rupees.
 */
export type ForeignPathwayPrice = {
  /** `pricing_skus.code` of the SKU sold in this currency. */
  sku: string;
  /** Whole-unit sticker price (e.g. 49 for $49). */
  amount: number;
  currency: string;
  skuType: string;
};

export const FOREIGN_PATHWAY_PRICING: Record<
  string,
  Record<string, ForeignPathwayPrice>
> = {
  USD: {
    // Falls back to the seeded US SKU so the first paint has a price.
    "esports-educator": {
      sku: "bundle_esports_educator_us",
      amount: 49,
      currency: "USD",
      skuType: "bundle",
    },
    "event-tournament-operations": {
      sku: "bundle_eventops_us",
      amount: 29,
      currency: "USD",
      skuType: "bundle",
    },
  },
};

/** Price for a pathway in a given currency; INR reads the main ladder. */
export const getPathwayPriceInCurrency = (
  slug: string | null | undefined,
  currency: string,
): ForeignPathwayPrice | null => {
  if (!slug) return null;
  const cur = (currency || "INR").toUpperCase();
  if (cur === "INR") {
    const p = getPathwayPricing(slug);
    if (!p) return null;
    return p.bundleOnly
      ? { sku: p.bundleSku, amount: p.bundleInr, currency: "INR", skuType: "bundle" }
      : { sku: p.pathwaySku, amount: p.pathwayInr, currency: "INR", skuType: "pathway" };
  }
  return FOREIGN_PATHWAY_PRICING[cur]?.[slug] ?? null;
};


export type PublicPromoRow = {
  code: string;
  discount_type: string;
  discount_value: number;
  applies_to_skus: string[] | null;
  partner_key?: string | null;
};

const toRupees = (paise: number | null | undefined) => {
  const rupees = Math.round(Number(paise ?? NaN) / 100);
  return Number.isFinite(rupees) && rupees > 0 ? rupees : null;
};

/** Latest promo rows, kept so price hydration can recompute promo prices. */
let LAST_PROMO_ROWS: PublicPromoRow[] = [];

const recomputePromos = (): boolean => {
  let changed = false;
  let headline: { code: string; priceInr: number } | null = null;

  for (const entry of Object.values(PATHWAY_PRICING)) {
    const promo = LAST_PROMO_ROWS.find((p) =>
      (p.applies_to_skus ?? []).includes(entry.pathwaySku),
    );
    let next: PathwayPricing["launchPromo"];
    if (promo) {
      let priceInr: number;
      if (promo.discount_type === "target") {
        priceInr = Math.max(0, Math.round(promo.discount_value / 100));
      } else if (promo.discount_type === "percent") {
        const off = Math.round((entry.pathwayInr * promo.discount_value) / 100);
        priceInr = Math.max(0, entry.pathwayInr - off);
      } else {
        const off = Math.round(promo.discount_value / 100);
        priceInr = Math.max(0, entry.pathwayInr - off);
      }
      // Never advertise a free (₹0) code on marketing surfaces even if mis-flagged.
      if (priceInr < entry.pathwayInr && priceInr > 0) {
        next = { code: promo.code, priceInr };
      }
    }
    const before = JSON.stringify(entry.launchPromo ?? null);
    if (before !== JSON.stringify(next ?? null)) {
      entry.launchPromo = next;
      changed = true;
    }
    if (next && (!headline || next.priceInr < headline.priceInr)) headline = next;
  }

  if (JSON.stringify(ACTIVE_PROMO) !== JSON.stringify(headline)) {
    ACTIVE_PROMO = headline;
    changed = true;
  }
  return changed;
};

/**
 * Fold the authoritative SKU rows into the live map. Pathways are discovered
 * from the rows themselves, so a new pathway priced in the admin shows up
 * without a code change.
 *
 * Returns true when at least one displayed price actually changed, so callers
 * can decide whether a re-render is needed.
 */
export const applyPricingSkus = (allRows: PricingSkuRow[]): boolean => {
  let changed = false;

  const currencyOf = (r: PricingSkuRow) =>
    (r.currency ?? "INR").toUpperCase() || "INR";

  // Non-INR rows live in their own map — never folded into the rupee ladder.
  for (const row of allRows) {
    const cur = currencyOf(row);
    if (cur === "INR" || !row.pathway_slug) continue;
    const amount = Math.round(Number(row.amount_paise ?? NaN) / 100);
    if (!Number.isFinite(amount) || amount <= 0) continue;
    const bucket = (FOREIGN_PATHWAY_PRICING[cur] ??= {});
    const before = bucket[row.pathway_slug];
    if (
      !before ||
      before.sku !== row.code ||
      before.amount !== amount ||
      before.skuType !== row.sku_type
    ) {
      bucket[row.pathway_slug] = {
        sku: row.code,
        amount,
        currency: cur,
        skuType: row.sku_type,
      };
      changed = true;
    }
  }

  const rows = allRows.filter((r) => currencyOf(r) === "INR");

  const slugs = new Set(
    rows.map((r) => r.pathway_slug).filter((s): s is string => Boolean(s)),
  );
  for (const slug of Object.keys(PATHWAY_PRICING)) slugs.add(slug);


  for (const slug of slugs) {
    const forSlug = rows.filter((r) => r.pathway_slug === slug);
    const existing = PATHWAY_PRICING[slug];
    const pick = (type: string, fallbackCode?: string) =>
      forSlug.find((r) => r.sku_type === type) ??
      (fallbackCode ? forSlug.find((r) => r.code === fallbackCode) : undefined);

    const pathwayRow = pick("pathway", existing?.pathwaySku);
    const certRow = pick("certificate", existing?.certificateSku);
    const bundleRow = pick("bundle", existing?.bundleSku);

    if (!existing) {
      if (!pathwayRow) continue;
      PATHWAY_PRICING[slug] = {
        pathwaySku: pathwayRow.code,
        certificateSku: certRow?.code ?? "",
        bundleSku: bundleRow?.code ?? "",
        pathwayInr: toRupees(pathwayRow.amount_paise) ?? FLAT_PRICING.pathwayInr,
        certificateInr:
          toRupees(certRow?.amount_paise) ?? FLAT_PRICING.certificateInr,
        bundleInr: toRupees(bundleRow?.amount_paise) ?? FLAT_PRICING.bundleInr,
      };
      changed = true;
      continue;
    }

    const updates: Array<[keyof PathwayPricing, string | null, number | null]> = [
      ["pathwaySku", pathwayRow?.code ?? null, null],
      ["certificateSku", certRow?.code ?? null, null],
      ["bundleSku", bundleRow?.code ?? null, null],
      ["pathwayInr", null, toRupees(pathwayRow?.amount_paise)],
      ["certificateInr", null, toRupees(certRow?.amount_paise)],
      ["bundleInr", null, toRupees(bundleRow?.amount_paise)],
    ];
    for (const [field, code, rupees] of updates) {
      const value = code ?? rupees;
      if (value == null) continue;
      if (existing[field] !== value) {
        (existing[field] as string | number) = value;
        changed = true;
      }
    }
  }

  return recomputePromos() || changed;
};

/**
 * Fold currently-running promo codes into the live map. Called by
 * `usePricingSkus()` with rows from `public.public_promos`.
 */
export const applyPromoCodes = (rows: PublicPromoRow[]): boolean => {
  LAST_PROMO_ROWS = rows;
  return recomputePromos();
};

/** Every pathway slug that currently has a published price. */
export const pricedPathwaySlugs = () => Object.keys(PATHWAY_PRICING);

export const getPathwayPricing = (slug?: string | null): PathwayPricing | null =>
  (slug && PATHWAY_PRICING[slug]) || null;

/**
 * SKU code used at checkout for a pathway unlock. For bundle-only (WORK-band)
 * pathways this is the combined pathway+certificate SKU — the only thing sold.
 */
export const getPathwaySku = (slug?: string | null) => {
  const p = getPathwayPricing(slug);
  if (!p) return null;
  return p.bundleOnly ? p.bundleSku || null : p.pathwaySku || null;
};


/** ₹1,299 style formatting for whole-rupee amounts. */
export const formatInr = (rupees: number) =>
  `₹${rupees.toLocaleString("en-IN")}`;

/**
 * ₹1,299 style formatting for paise amounts coming back from orders /
 * payments. Same output shape as `formatInr` so checkout, receipts, refunds
 * and modal CTAs can never drift from catalog and pathway detail pricing.
 */
export const formatInrPaise = (paise: number | null | undefined) =>
  formatInr(Math.round((paise ?? 0) / 100));

/** $49 style formatting for whole-dollar amounts. */
export const formatUsd = (dollars: number) =>
  `$${dollars.toLocaleString("en-US")}`;

/** Format a whole-unit amount in whichever currency it is priced in. */
export const formatMoney = (amount: number, currency: string) =>
  (currency || "INR").toUpperCase() === "USD"
    ? formatUsd(amount)
    : formatInr(amount);




/** GST component of a GST-inclusive rupee amount (18%). */
export const gstComponentInr = (inclusiveRupees: number) =>
  Math.round((inclusiveRupees * 18) / 118);

/**
 * Legacy paid-unlock preview copy. This is not a generic offer definition:
 * current free PLAY and Parent Guide use their product-specific offer copy.
 */
export const FREE_PREVIEW_COPY =
  "Read the free preview, no account needed. Full path access is a one-time unlock.";
